import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, UserPlus, X } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { DEPARTMENT_BY_VALUE } from '@/lib/departments'
import { cn } from '@/lib/utils'
import { CreateDepartmentModal, type DirectoryDepartment } from './CreateDepartmentModal'
import { CreatePersonModal, type DirectoryPerson } from './CreatePersonModal'

export interface DirectoryResponse {
  departments: DirectoryDepartment[]
  people: DirectoryPerson[]
}

export interface DirectoryPanelProps {
  open: boolean
  onClose: () => void
}

/**
 * Slide-over listing every artist- and organisation-scope department and
 * person that email routing rules can target, with entry points to create
 * new ones. Toggled from the toolbar in EmailRulesPage / RoutingFlowCanvas.
 */
export function DirectoryPanel({ open, onClose }: DirectoryPanelProps) {
  const qc = useQueryClient()
  const [showCreateDept, setShowCreateDept] = useState(false)
  const [showCreatePerson, setShowCreatePerson] = useState(false)

  const { data, isLoading, error } = useQuery<DirectoryResponse, ApiClientError>({
    queryKey: ['email-directory'],
    queryFn: () => artistFetch<DirectoryResponse>('/email-directory'),
    enabled: open,
  })

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const departments = data?.departments ?? []
  const people = data?.people ?? []

  function departmentName(id: string | null): string | null {
    if (!id) return null
    return departments.find((d) => d.id === id)?.name ?? null
  }

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['email-directory'] })
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-ovation-ink/30 backdrop-blur-[2px]"
        aria-label="Close directory"
        onClick={onClose}
      />
      <aside className="relative flex h-full w-full max-w-md flex-col border-l border-ovation-ink/10 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-ovation-ink/10 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-ovation-ink">People &amp; departments</h2>
            <p className="text-xs text-ovation-muted">Everyone routing rules can target</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-ovation-muted hover:bg-ovation-ink/5 hover:text-ovation-ink"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-4">
          {isLoading && <p className="text-sm text-ovation-muted">Loading…</p>}
          {error && <p className="text-sm text-red-700">{error.message}</p>}

          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-xs font-medium uppercase tracking-wider text-ovation-muted">
                Departments ({departments.length})
              </h3>
              <button
                type="button"
                onClick={() => setShowCreateDept(true)}
                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
              >
                <Plus className="h-3 w-3" /> Add
              </button>
            </div>
            {departments.length === 0 ? (
              <p className="text-xs text-ovation-muted">No departments yet.</p>
            ) : (
              <ul className="space-y-1.5">
                {departments.map((d) => (
                  <li
                    key={`${d.scope}-${d.id}`}
                    className="rounded-lg border border-ovation-ink/10 px-3 py-2 text-sm"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-medium text-ovation-ink">{d.name}</span>
                      <span
                        className={cn(
                          'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                          d.scope === 'organisation'
                            ? 'bg-violet-100 text-violet-800'
                            : 'bg-ovation-ink/5 text-ovation-muted',
                        )}
                      >
                        {d.scope === 'organisation' ? 'Org' : 'Artist'}
                      </span>
                    </div>
                    {d.groupEmail && (
                      <div className="mt-0.5 truncate text-xs text-ovation-muted">{d.groupEmail}</div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-xs font-medium uppercase tracking-wider text-ovation-muted">
                People ({people.length})
              </h3>
              <button
                type="button"
                onClick={() => setShowCreatePerson(true)}
                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
              >
                <UserPlus className="h-3 w-3" /> Add
              </button>
            </div>
            {people.length === 0 ? (
              <p className="text-xs text-ovation-muted">No people yet.</p>
            ) : (
              <ul className="space-y-1.5">
                {people.map((p) => {
                  const deptLabel =
                    departmentName(p.departmentId) ??
                    (p.departments.length > 0
                      ? p.departments.map((dep) => DEPARTMENT_BY_VALUE[dep]?.label ?? dep).join(', ')
                      : null)
                  return (
                    <li
                      key={`${p.scope}-${p.id}`}
                      className="rounded-lg border border-ovation-ink/10 px-3 py-2 text-sm"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate font-medium text-ovation-ink">{p.name}</span>
                        <div className="flex shrink-0 items-center gap-1">
                          {p.isHead && (
                            <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                              Head
                            </span>
                          )}
                          <span
                            className={cn(
                              'rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                              p.scope === 'organisation'
                                ? 'bg-violet-100 text-violet-800'
                                : 'bg-ovation-ink/5 text-ovation-muted',
                            )}
                          >
                            {p.scope === 'organisation' ? 'Org' : 'Artist'}
                          </span>
                        </div>
                      </div>
                      <div className="mt-0.5 truncate text-xs text-ovation-muted">
                        {p.email}
                        {p.title && <> · {p.title}</>}
                      </div>
                      {deptLabel && (
                        <div className="mt-0.5 truncate text-[11px] text-ovation-muted">{deptLabel}</div>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </section>
        </div>
      </aside>

      {showCreateDept && (
        <CreateDepartmentModal
          open={showCreateDept}
          onClose={() => setShowCreateDept(false)}
          onCreated={() => {
            invalidate()
            setShowCreateDept(false)
          }}
        />
      )}
      {showCreatePerson && (
        <CreatePersonModal
          open={showCreatePerson}
          departments={departments}
          onClose={() => setShowCreatePerson(false)}
          onCreated={() => {
            invalidate()
            setShowCreatePerson(false)
          }}
        />
      )}
    </div>
  )
}
